// client/src/components/common/SubscriptionStatusModal.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  X,
  ShieldCheck,
  ShieldOff,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Car,
  Wrench,
  MessageSquare,
  PhoneCall,
  Database,
  Settings2,
  CreditCard
} from 'lucide-react';
import { apiClient } from '../../services/apiClient';

const INTEGRATIONS = [
  {
    key: 'vinsolutions',
    label: 'CRM',
    description: 'Customer lookups, contact creation, and CRM sync.',
    Icon: Car
  },
  {
    key: 'twilio',
    label: 'Twilio',
    description: 'Twilio lookup for phonenumber',
    Icon: MessageSquare
  },
  {
    key: 'call',
    label: 'Call Notifications',
    description: 'Real-time call events over WebSocket.',
    Icon: PhoneCall
  }
];

const DMS_PROVIDERS = [
  {
    key: 'tekion',
    label: 'Tekion',
    description: 'Repair orders, parts/sales orders, and deals.',
    Icon: Wrench
  },
  {
    key: 'dealertrack',
    label: 'Dealertrack',
    description: 'Dealertrack DMS integration.',
    Icon: Settings2,
    // API not yet available — keep the row visible but force the switch off
    // and non-interactive. Flip this off once the integration ships.
    comingSoon: true
  }
];

// Independent self-service toggles gated only by the master subscription —
// NOT tied to DMS/Tekion. Payment Link lives here so tenants can use it
// regardless of which (if any) DMS provider is enabled.
const INDEPENDENT_TOGGLES = [
  {
    key: 'paymentLink',
    label: 'Payment Link',
    description: 'Send payment links to customers for repair and parts orders.',
    Icon: CreditCard
  }
];

// Visual switch styled to look like a toggle. `disabled` blocks clicks and dims it.
const ToggleSwitch = ({ checked, disabled, onChange, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={[
      'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors',
      'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1',
      disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      checked ? 'bg-green-500' : 'bg-gray-300'
    ].join(' ')}
  >
    <span
      className={[
        'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
        'translate-y-0.5',
        checked ? 'translate-x-4' : 'translate-x-0.5'
      ].join(' ')}
    />
  </button>
);

const FlagRow = ({ Icon, label, description, state }) => {
  // state: 'enabled' | 'disabled' | 'blocked'
  //   enabled — subscription active AND flag true
  //   disabled — subscription active AND flag false
  //   blocked — subscription inactive or missing; flag is effectively off
  const styles = {
    enabled: {
      badge: 'bg-green-100 text-green-700 border-green-200',
      badgeText: 'Enabled',
      BadgeIcon: CheckCircle2,
      iconWrap: 'bg-green-50 text-green-600'
    },
    disabled: {
      badge: 'bg-gray-100 text-gray-600 border-gray-200',
      badgeText: 'Disabled',
      BadgeIcon: XCircle,
      iconWrap: 'bg-gray-50 text-gray-500'
    },
    blocked: {
      badge: 'bg-amber-50 text-amber-700 border-amber-200',
      badgeText: 'Unavailable',
      BadgeIcon: AlertCircle,
      iconWrap: 'bg-gray-50 text-gray-400'
    }
  }[state];

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${styles.iconWrap}`}>
        <Icon className="w-4.5 h-4.5" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-gray-900 truncate">{label}</h4>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${styles.badge}`}>
            <styles.BadgeIcon className="w-3 h-3" />
            {styles.badgeText}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        {state === 'blocked' && (
          <p className="text-xs text-amber-600 mt-1 italic">
            Master subscription is not active — this integration is gated off.
          </p>
        )}
      </div>
    </div>
  );
};

const SubscriptionStatusModal = ({ isOpen, onClose, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/ext-subscription/me');
      if (response.data && response.data.success) {
        setData(response.data);
      } else {
        setError(response.data?.message || 'Failed to load subscription details.');
      }
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 401 || status === 403) {
        setError('Your session is not authorized to view subscription details.');
      } else {
        setError(message || 'Could not reach the subscription service. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // PATCH a single self-service flag (DMS providers tekion / dealertrack, or the
  // independent paymentLink) for the current user. Optimistic update with
  // rollback on failure.
  const patchFlag = useCallback(async (key, nextValue) => {
    if (!data?.item) return;
    setSavingKey(key);
    setSaveError(null);
    const prevItem = data.item;
    const optimisticItem = { ...prevItem, [key]: nextValue };
    setData((d) => (d ? { ...d, item: optimisticItem } : d));
    try {
      const response = await apiClient.patch('/ext-subscription/me', { [key]: nextValue });
      if (response.data && response.data.success) {
        setData((d) => (d ? { ...d, item: response.data.item, access: response.data.access } : d));
      } else {
        setData((d) => (d ? { ...d, item: prevItem } : d));
        setSaveError(response.data?.message || 'Failed to update toggle.');
      }
    } catch (err) {
      setData((d) => (d ? { ...d, item: prevItem } : d));
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 401 || status === 403) {
        setSaveError('Not authorized to update this subscription.');
      } else {
        setSaveError(message || 'Could not save changes. Please try again.');
      }
    } finally {
      setSavingKey(null);
    }
  }, [data]);

  // Fetch every time the modal opens so the data is always fresh.
  useEffect(() => {
    if (isOpen) {
      setSaveError(null);
      fetchSubscription();
    }
  }, [isOpen, fetchSubscription]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const item = data?.item ?? null;
  const ext = data?.ext ?? currentUser?.username ?? '—';
  const hasRow = !!item;
  const isActive = hasRow && item.subscription === 'active';

  // Master subscription card styling
  const masterStyles = isActive
    ? { wrap: 'bg-green-50 border-green-200', icon: 'text-green-600', title: 'text-green-900', badge: 'bg-green-600 text-white' }
    : hasRow
      ? { wrap: 'bg-red-50 border-red-200', icon: 'text-red-600', title: 'text-red-900', badge: 'bg-red-600 text-white' }
      : { wrap: 'bg-gray-50 border-gray-200', icon: 'text-gray-500', title: 'text-gray-800', badge: 'bg-gray-500 text-white' };

  // Computed per-flag display state given current data
  const flagState = (key) => {
    if (!hasRow) return 'blocked';
    if (!isActive) return 'blocked';
    return item[key] ? 'enabled' : 'disabled';
  };

  // Count enabled integrations for the summary line. DMS counts as one if
  // any DMS provider is on (parent gate respected via flagState). Independent
  // toggles (e.g. Payment Link) are counted on their own flag, not via DMS.
  const enabledCount = isActive
    ? INTEGRATIONS.filter((i) => !!item[i.key]).length +
      INDEPENDENT_TOGGLES.filter((i) => !!item[i.key]).length +
      (item.dms && DMS_PROVIDERS.some((p) => !!item[p.key]) ? 1 : 0)
    : 0;
  const integrationTotal = INTEGRATIONS.length + INDEPENDENT_TOGGLES.length + 1; // +1 for DMS group

  const dmsOn = hasRow && isActive && !!item.dms;
  // DMS display state mirrors FlagRow: enabled | disabled | blocked
  const dmsState = !hasRow || !isActive ? 'blocked' : (item.dms ? 'enabled' : 'disabled');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            <h2 id="subscription-modal-title" className="text-base font-semibold text-gray-900">
              My Subscriptions
            </h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={fetchSubscription}
              disabled={loading}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh"
              aria-label="Refresh subscription status"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Extension banner */}
          <div className="text-xs text-gray-500 mb-3">
            Showing subscription for extension <span className="font-semibold text-gray-700">{ext}</span>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-10 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mb-2 text-blue-500" />
              <p className="text-sm">Loading subscription details…</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Couldn't load subscription</p>
                <p className="text-xs text-red-700 mt-1">{error}</p>
                <button
                  onClick={fetchSubscription}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Master subscription card */}
              <div className={`rounded-lg border p-4 mb-4 ${masterStyles.wrap}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {isActive ? (
                      <ShieldCheck className={`w-6 h-6 ${masterStyles.icon} flex-shrink-0`} />
                    ) : (
                      <ShieldOff className={`w-6 h-6 ${masterStyles.icon} flex-shrink-0`} />
                    )}
                    <div>
                      <h3 className={`text-sm font-semibold ${masterStyles.title}`}>
                        Master Subscription
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {!hasRow && 'No subscription record exists for this extension.'}
                        {hasRow && isActive && 'Your extension has an active subscription.'}
                        {hasRow && !isActive && 'Your subscription is currently inactive.'}
                      </p>
                    </div>
                  </div>
                  <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${masterStyles.badge}`}>
                    {!hasRow ? 'Not Subscribed' : isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Helper line under master */}
                {hasRow && isActive && (
                  <p className="text-xs text-green-700 mt-3">
                    {enabledCount === 0
                      ? 'Subscription is active, but no individual integrations are enabled.'
                      : `${enabledCount} of ${integrationTotal} integrations enabled.`}
                  </p>
                )}
                {hasRow && !isActive && (
                  <p className="text-xs text-red-700 mt-3">
                    All integrations are gated off while the master subscription is inactive.
                  </p>
                )}
                {!hasRow && (
                  <p className="text-xs text-gray-600 mt-3">
                    Contact your system administrator to enable a subscription for your extension.
                  </p>
                )}
              </div>

              {/* Inline save error */}
              {saveError && (
                <div className="mb-3 flex items-start gap-2 p-2.5 rounded-md bg-red-50 border border-red-200">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 flex-1">{saveError}</p>
                </div>
              )}

              {/* Integration list */}
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
                  Integrations
                </h3>
                {INTEGRATIONS.map(({ key, label, description, Icon }) => (
                  <FlagRow
                    key={key}
                    Icon={Icon}
                    label={label}
                    description={description}
                    state={flagState(key)}
                  />
                ))}

                {/* DMS section: Tekion / Dealertrack toggles only.
                    DMS itself is admin-controlled — when DMS is off, both
                    sub-toggles are disabled with an admin-contact hint. */}
                <div className="rounded-lg border border-gray-100 p-3">
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${dmsOn ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'}`}>
                      <Database className="w-4.5 h-4.5" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-semibold text-gray-900">DMS</h4>
                        {(() => {
                          const s = {
                            enabled: { cls: 'bg-green-100 text-green-700 border-green-200', text: 'Enabled', BadgeIcon: CheckCircle2 },
                            disabled: { cls: 'bg-gray-100 text-gray-600 border-gray-200', text: 'Disabled', BadgeIcon: XCircle },
                            blocked: { cls: 'bg-amber-50 text-amber-700 border-amber-200', text: 'Unavailable', BadgeIcon: AlertCircle }
                          }[dmsState];
                          return (
                            <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}>
                              <s.BadgeIcon className="w-3 h-3" />
                              {s.text}
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Choose which DMS providers are active for your extension.
                      </p>
                      {hasRow && isActive && !dmsOn && (
                        <p className="text-xs text-amber-600 mt-1 italic">
                          DMS access is not enabled for your extension. Contact your administrator.
                        </p>
                      )}
                      {hasRow && !isActive && (
                        <p className="text-xs text-amber-600 mt-1 italic">
                          Master subscription is not active — DMS is gated off.
                        </p>
                      )}

                      {/* Provider toggles */}
                      <div className="mt-3 pl-3 border-l-2 border-gray-100 space-y-2">
                        {DMS_PROVIDERS.map(({ key, label, description, Icon, comingSoon }) => {
                          const subDisabled = comingSoon || !isActive || !dmsOn || savingKey === key;
                          const subChecked = !comingSoon && !!(hasRow && isActive && dmsOn && item[key]);
                          return (
                            <div key={key} className="flex items-start gap-3 p-2 rounded-md">
                              <div className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${subChecked ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-400'}`}>
                                <Icon className="w-4 h-4" strokeWidth={2} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <h5 className="text-sm font-medium text-gray-800">{label}</h5>
                                    {comingSoon && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-gray-200 bg-gray-50 text-gray-500">
                                        Coming soon
                                      </span>
                                    )}
                                  </div>
                                  <ToggleSwitch
                                    checked={subChecked}
                                    disabled={subDisabled}
                                    onChange={(v) => patchFlag(key, v)}
                                    ariaLabel={`Toggle ${label} access`}
                                  />
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Independent self-service toggles (Payment Link). Gated only
                    by the master subscription — not by DMS/Tekion. */}
                {INDEPENDENT_TOGGLES.map(({ key, label, description, Icon }) => {
                  const toggleDisabled = !isActive || savingKey === key;
                  const toggleChecked = !!(hasRow && isActive && item[key]);
                  return (
                    <div key={key} className="rounded-lg border border-gray-100 p-3">
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${toggleChecked ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'}`}>
                          <Icon className="w-4.5 h-4.5" strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="text-sm font-semibold text-gray-900">{label}</h4>
                            <ToggleSwitch
                              checked={toggleChecked}
                              disabled={toggleDisabled}
                              onChange={(v) => patchFlag(key, v)}
                              ariaLabel={`Toggle ${label} access`}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                          {hasRow && !isActive && (
                            <p className="text-xs text-amber-600 mt-1 italic">
                              Master subscription is not active — this integration is gated off.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer timestamps */}
              {hasRow && (item.modified_time || item.created_time) && (
                <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400 space-y-0.5">
                  {item.created_time && (
                    <div>Created: {new Date(item.created_time).toLocaleString()}</div>
                  )}
                  {item.modified_time && (
                    <div>Last updated: {new Date(item.modified_time).toLocaleString()}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatusModal;
